import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Ban,
  CheckCircle2,
  ChevronLeft,
  ChevronRight,
  Eye,
  Images,
  Loader2,
  Search,
  ShieldAlert,
  ShieldCheck,
  Smartphone,
  UserCircle,
  X,
} from 'lucide-react';
import BrandLogo from '../components/BrandLogo';
import adminService from '../services/adminService';

const STATUS_OPTIONS = [
  { label: 'All', value: 'all' },
  { label: 'Active', value: 'active' },
  { label: 'Banned', value: 'banned' },
  { label: 'Admins', value: 'admins' },
];

const COUNTRY_OPTIONS = [
  { label: 'All countries', value: '' },
  { label: 'Malawi', value: 'Malawi' },
  { label: 'Zambia', value: 'Zambia' },
  { label: 'Zimbabwe', value: 'Zimbabwe' },
  { label: 'Mozambique', value: 'Mozambique' },
  { label: 'Tanzania', value: 'Tanzania' },
  { label: 'South Africa', value: 'South Africa' },
  { label: 'Botswana', value: 'Botswana' },
  { label: 'Namibia', value: 'Namibia' },
  { label: 'Uganda', value: 'Uganda' },
  { label: 'Kenya', value: 'Kenya' },
  { label: 'Nigeria', value: 'Nigeria' },
  { label: 'Ghana', value: 'Ghana' },
  { label: 'United States', value: 'United States' },
  { label: 'United Kingdom', value: 'United Kingdom' },
];

export default function UserList({ focusUserId, onStatsChanged }) {
  const [users, setUsers] = useState([]);
  const [pagination, setPagination] = useState({ page: 1, limit: 25, total: 0, totalPages: 1 });
  const [status, setStatus] = useState('all');
  const [search, setSearch] = useState('');
  const [countryOfResidence, setCountryOfResidence] = useState('');
  const [loading, setLoading] = useState(true);
  const [actionUserId, setActionUserId] = useState('');
  const [error, setError] = useState('');
  const [selectedUser, setSelectedUser] = useState(null);
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [banDialogUser, setBanDialogUser] = useState(null);
  const [unbanDialogUser, setUnbanDialogUser] = useState(null);
  const [reminderDialogOpen, setReminderDialogOpen] = useState(false);
  const [reminderState, setReminderState] = useState({
    loading: false,
    message: '',
    error: '',
  });
  const prevFocusUserId = useRef(null);

  const page = pagination.page || 1;
  const totalPages = pagination.totalPages || 1;

  const query = useMemo(
    () => ({
      page,
      limit: pagination.limit || 25,
      status,
      search,
      countryOfResidence,
    }),
    [countryOfResidence, page, pagination.limit, search, status],
  );

  const loadUsers = useCallback(async (params = query) => {
    setLoading(true);
    setError('');
    try {
      const result = await adminService.getUsers(params);
      setUsers(result.data);
      setPagination(result.pagination);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [query]);

  useEffect(() => {
    loadUsers(query);
  }, [loadUsers, query]);

  useEffect(() => {
    if (focusUserId && focusUserId !== prevFocusUserId.current) {
      prevFocusUserId.current = focusUserId;
      openDetails(focusUserId);
    }
  }, [focusUserId]);

  const goToPage = (nextPage) => {
    setPagination((current) => ({
      ...current,
      page: Math.min(Math.max(1, nextPage), current.totalPages || 1),
    }));
  };

  const handleFilter = (nextStatus) => {
    setStatus(nextStatus);
    setPagination((current) => ({ ...current, page: 1 }));
  };

  const handleSearchSubmit = (event) => {
    event.preventDefault();
    setPagination((current) => ({ ...current, page: 1 }));
    loadUsers({ ...query, page: 1 });
  };

  const openDetails = async (userId) => {
    setDetailsLoading(true);
    setSelectedUser(null);
    try {
      const user = await adminService.getUser(userId);
      setSelectedUser(user);
    } catch (err) {
      setError(err.message);
    } finally {
      setDetailsLoading(false);
    }
  };

  const requestBanToggle = (user) => {
    const isBanned = user.isBanned || user.status === 'BLOCKED';
    if (isBanned) {
      setUnbanDialogUser(user);
      return;
    }
    setBanDialogUser(user);
  };

  const confirmBan = async (user, reason) => {
    setActionUserId(user.id);
    setError('');
    try {
      await adminService.banUser(user.id, reason);
      await loadUsers(query);
      if (selectedUser?.id === user.id) {
        await openDetails(user.id);
      }
      setBanDialogUser(null);
      onStatsChanged?.();
    } catch (err) {
      setError(err.message);
    } finally {
      setActionUserId('');
    }
  };

  const confirmUnban = async (user) => {
    setActionUserId(user.id);
    setError('');
    try {
      await adminService.unbanUser(user.id);
      await loadUsers(query);
      if (selectedUser?.id === user.id) {
        await openDetails(user.id);
      }
      setUnbanDialogUser(null);
      onStatsChanged?.();
    } catch (err) {
      setError(err.message);
    } finally {
      setActionUserId('');
    }
  };

  const sendSubscriptionReminders = async ({ title, body } = {}) => {
    setReminderState({ loading: true, message: '', error: '' });
    try {
      const result = await adminService.sendSubscriptionReminders({
        title,
        body,
      });
      setReminderState({
        loading: false,
        error: '',
        message: result.queued
          ? `Queued reminders for ${result.targetedUsers || 0} users. Delivery will continue in the background.`
          : `Sent to ${result.targetedUsers || 0} users: ${result.pushSent || 0} push, ${result.emailSent || 0} email.`,
      });
    } catch (err) {
      setReminderState({
        loading: false,
        message: '',
        error: err.message || 'Could not send subscription reminders.',
      });
    }
  };

  return (
    <div className="space-y-6">
      <div className="grid gap-4 rounded-lg border border-[#E2E8F0] bg-white p-4 shadow-sm xl:grid-cols-[1fr_420px]">
        <div className="flex flex-wrap gap-2">
          {STATUS_OPTIONS.map((option) => (
            <button
              key={option.value}
              className={`rounded-lg px-4 py-2 text-xs font-semibold transition-all ${
                status === option.value
                  ? 'bg-[#0F172A] text-white'
                  : 'bg-[#F8FAFC] text-[#64748B] border border-[#E2E8F0] hover:bg-[#F1F5F9]'
              }`}
              onClick={() => handleFilter(option.value)}
            >
              {option.label}
            </button>
          ))}
        </div>

        <form className="grid w-full gap-2 sm:grid-cols-[1fr_190px_auto]" onSubmit={handleSearchSubmit}>
          <div className="flex flex-1 items-center gap-2 rounded-lg border border-[#E2E8F0] bg-[#F8FAFC] px-3 focus-within:ring-2 focus-within:ring-[#D91F26]/20 focus-within:border-[#D91F26]">
            <Search size={16} className="text-[#94A3B8]" />
            <input
              className="w-full bg-transparent py-2.5 text-sm font-medium outline-none placeholder:text-[#94A3B8]"
              placeholder="Search name, email or phone"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
            />
          </div>
          <select
            aria-label="Filter by country"
            className="rounded-lg border border-[#E2E8F0] bg-[#F8FAFC] px-3 py-2.5 text-sm font-medium text-[#0F172A] outline-none focus:border-[#D91F26] focus:ring-2 focus:ring-[#D91F26]/20"
            value={countryOfResidence}
            onChange={(event) => {
              setCountryOfResidence(event.target.value);
              setPagination((current) => ({ ...current, page: 1 }));
            }}
          >
            {COUNTRY_OPTIONS.map((country) => (
              <option key={country.label} value={country.value}>
                {country.label}
              </option>
            ))}
          </select>
          <button className="rounded-lg bg-[#D91F26] px-5 py-2.5 text-sm font-semibold text-white hover:bg-red-700 transition-all active:scale-[0.98]">
            Search
          </button>
        </form>
      </div>

      {error && (
        <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm font-semibold text-[#D91F26]">
          {error}
        </div>
      )}

      <div className="overflow-hidden rounded-lg border border-[#E2E8F0] bg-white shadow-sm">
        <div className="flex items-center justify-between border-b border-[#E2E8F0] px-6 pt-6 pb-4">
          <div>
            <p className="text-xs font-medium text-[#94A3B8] uppercase tracking-wider">
              User Management
            </p>
            <p className="text-sm font-semibold text-[#0F172A]">{pagination.total || 0} records</p>
          </div>
          <div className="flex flex-wrap items-center justify-end gap-3">
            {(reminderState.message || reminderState.error) && (
              <p className={`max-w-sm rounded-lg border px-3 py-2 text-xs font-semibold ${
                reminderState.error
                  ? 'border-red-100 bg-red-50 text-[#D91F26]'
                  : 'border-emerald-100 bg-emerald-50 text-emerald-700'
              }`}>
                {reminderState.error || reminderState.message}
              </p>
            )}
            <button
              className="inline-flex items-center gap-2 rounded-lg bg-[#0F172A] px-4 py-2.5 text-xs font-semibold text-white transition-all hover:bg-slate-700 active:scale-[0.98] disabled:cursor-not-allowed disabled:opacity-60"
              disabled={reminderState.loading}
              onClick={() => setReminderDialogOpen(true)}
            >
              {reminderState.loading ? (
                <Loader2 className="animate-spin" size={15} />
              ) : (
                <ShieldCheck size={15} />
              )}
              {reminderState.loading ? 'Sending reminders' : 'Send reminders'}
            </button>
            {loading && <Loader2 className="animate-spin text-[#94A3B8]" size={18} />}
          </div>
        </div>

        <div className="divide-y divide-[#E2E8F0]">
          {users.map((user) => (
            <UserRow
              key={user.id}
              actionUserId={actionUserId}
              onDetails={openDetails}
              onToggleBan={requestBanToggle}
              user={user}
            />
          ))}
        </div>

        {!loading && users.length === 0 && (
          <div className="px-6 py-12 text-center text-sm font-medium text-[#94A3B8]">
            No users found.
          </div>
        )}

        <div className="flex items-center justify-between border-t border-[#E2E8F0] px-6 py-3">
          <p className="text-xs font-medium text-[#94A3B8]">
            Page {page} of {totalPages}
          </p>
          <div className="flex gap-2">
            <button
              className="rounded-md border border-[#E2E8F0] p-2 text-[#64748B] hover:bg-[#F1F5F9] transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
              disabled={page <= 1}
              onClick={() => goToPage(page - 1)}
            >
              <ChevronLeft size={16} />
            </button>
            <button
              className="rounded-md border border-[#E2E8F0] p-2 text-[#64748B] hover:bg-[#F1F5F9] transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
              disabled={page >= totalPages}
              onClick={() => goToPage(page + 1)}
            >
              <ChevronRight size={16} />
            </button>
          </div>
        </div>
      </div>

      {(selectedUser || detailsLoading) && (
        <UserDetailsDrawer
          loading={detailsLoading}
          onClose={() => setSelectedUser(null)}
          onToggleBan={requestBanToggle}
          user={selectedUser}
        />
      )}
      {banDialogUser && (
        <BanReasonDialog
          loading={actionUserId === banDialogUser.id}
          onCancel={() => setBanDialogUser(null)}
          onConfirm={(reason) => confirmBan(banDialogUser, reason)}
          user={banDialogUser}
        />
      )}
      {unbanDialogUser && (
        <ConfirmUserDialog
          confirmLabel="Unban user"
          loading={actionUserId === unbanDialogUser.id}
          message="This will restore the account to active status and allow the user to use the app again."
          onCancel={() => setUnbanDialogUser(null)}
          onConfirm={() => confirmUnban(unbanDialogUser)}
          title={`Unban ${unbanDialogUser.displayName}?`}
        />
      )}
      {reminderDialogOpen && (
        <SubscriptionReminderDialog
          loading={reminderState.loading}
          onCancel={() => setReminderDialogOpen(false)}
          onConfirm={async (payload) => {
            await sendSubscriptionReminders(payload);
            setReminderDialogOpen(false);
          }}
        />
      )}
    </div>
  );
}

function SubscriptionReminderDialog({ loading, onCancel, onConfirm }) {
  const [title, setTitle] = useState('Subscribe to unlock chat');
  const [body, setBody] = useState(
    'People are waiting to chat with you. Subscribe now to continue.',
  );
  const canSend = title.trim().length >= 3 && body.trim().length >= 8;

  return (
    <div className="fixed inset-0 z-[110] flex items-center justify-center bg-black/60 p-4 backdrop-blur-sm">
      <div className="w-full max-w-lg rounded-xl border border-[#E2E8F0] bg-white p-6 shadow-2xl">
        <DialogHeader title="Send subscription reminders" />
        <label className="block">
          <span className="text-xs font-medium uppercase tracking-wider text-[#94A3B8]">
            Push title / email subject
          </span>
          <input
            className="mt-2 w-full rounded-lg border border-[#E2E8F0] bg-[#F8FAFC] px-4 py-3 text-sm font-medium outline-none transition-all focus:border-[#D91F26] focus:ring-2 focus:ring-[#D91F26]/20"
            maxLength={80}
            onChange={(event) => setTitle(event.target.value)}
            value={title}
          />
        </label>
        <label className="mt-4 block">
          <span className="text-xs font-medium uppercase tracking-wider text-[#94A3B8]">
            Reminder message
          </span>
          <textarea
            className="mt-2 min-h-32 w-full resize-none rounded-lg border border-[#E2E8F0] bg-[#F8FAFC] p-4 text-sm font-medium outline-none transition-all focus:border-[#D91F26] focus:ring-2 focus:ring-[#D91F26]/20"
            maxLength={240}
            onChange={(event) => setBody(event.target.value)}
            value={body}
          />
        </label>
        <div className="mt-6 flex justify-end gap-3">
          <button
            className="rounded-lg border border-[#E2E8F0] bg-[#F8FAFC] px-4 py-2.5 text-sm font-semibold text-[#64748B] transition-all hover:bg-[#F1F5F9]"
            disabled={loading}
            onClick={onCancel}
          >
            Cancel
          </button>
          <button
            className="inline-flex items-center gap-2 rounded-lg bg-[#D91F26] px-4 py-2.5 text-sm font-semibold text-white transition-all hover:bg-red-700 active:scale-[0.98] disabled:cursor-not-allowed disabled:opacity-50"
            disabled={loading || !canSend}
            onClick={() => onConfirm({ title: title.trim(), body: body.trim() })}
          >
            {loading && <Loader2 className="animate-spin" size={16} />}
            {loading ? 'Sending...' : 'Send reminders'}
          </button>
        </div>
      </div>
    </div>
  );
}

function UserRow({ actionUserId, onDetails, onToggleBan, user }) {
  const banned = user.isBanned || user.status === 'BLOCKED';
  const risky = user.activeSessionCount >= 3 || user.activeDeviceCount >= 3 || user.reportsAgainstCount > 0;
  return (
    <div
      className="grid w-full cursor-pointer grid-cols-1 gap-4 px-6 py-4 text-left transition hover:bg-[#F8FAFC] lg:grid-cols-[minmax(0,1.5fr)_110px_110px_130px]"
      onClick={() => onDetails(user.id)}
      role="button"
      tabIndex={0}
    >
      <div className="min-w-0">
        <div className="flex min-w-0 items-center gap-3">
          <Avatar user={user} />
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <p className="truncate text-sm font-semibold text-[#0F172A]">{user.displayName}</p>
              {risky && <ShieldAlert size={15} className="shrink-0 text-[#D91F26]" />}
            </div>
            <p className="truncate text-xs font-medium text-[#94A3B8]">
              {[user.email || user.phone || user.id, user.countryOfResidence].filter(Boolean).join(' · ')}
            </p>
            <div className="mt-2 flex flex-wrap gap-2 lg:hidden">
              <AuthPill method={user.authMethod} />
              <StatusPill banned={banned} isAdmin={user.isAdmin} status={user.status} />
              <RiskSummary risky={risky} user={user} />
            </div>
          </div>
        </div>
      </div>

      <div className="hidden items-center lg:flex">
        <AuthPill method={user.authMethod} />
      </div>

      <div className="hidden items-center lg:flex">
        <StatusPill banned={banned} isAdmin={user.isAdmin} status={user.status} />
      </div>

      <div className="hidden items-center lg:flex">
        <RiskSummary risky={risky} user={user} />
      </div>

      <div
        className="flex items-center gap-2 lg:justify-end"
        onClick={(event) => event.stopPropagation()}
      >
        <IconActionButton label="View user profile" onClick={() => onDetails(user.id)}>
          <Eye size={16} />
        </IconActionButton>
        <IconActionButton
          disabled={actionUserId === user.id || user.isAdmin}
          label={
            user.isAdmin
              ? 'Admin users cannot be banned'
              : banned
              ? 'Unban this user'
              : 'Ban this user'
          }
          onClick={() => onToggleBan(user)}
          tone={banned ? 'success' : 'danger'}
        >
          {actionUserId === user.id ? (
            <Loader2 className="animate-spin" size={16} />
          ) : banned ? (
            <CheckCircle2 size={16} />
          ) : (
            <Ban size={16} />
          )}
        </IconActionButton>
      </div>
    </div>
  );
}

function IconActionButton({
  children,
  disabled = false,
  label,
  onClick,
  tone = 'neutral',
}) {
  const toneClass =
    tone === 'danger'
      ? 'border-red-100 text-[#D91F26] hover:bg-red-50'
      : tone === 'success'
      ? 'border-emerald-100 text-emerald-600 hover:bg-emerald-50'
      : 'border-[#E2E8F0] text-[#64748B] hover:bg-[#F1F5F9]';

  return (
    <div className="group relative inline-flex">
      <button
        aria-label={label}
        className={`rounded-md border p-2 transition-all disabled:cursor-not-allowed disabled:opacity-40 ${toneClass}`}
        disabled={disabled}
        onClick={onClick}
        title={label}
      >
        {children}
      </button>
      <span className="pointer-events-none absolute bottom-full right-0 z-20 mb-2 hidden whitespace-nowrap rounded-lg bg-[#0F172A] px-3 py-2 text-[10px] font-medium uppercase tracking-wider text-white shadow-xl group-hover:block">
        {label}
      </span>
    </div>
  );
}

function BanReasonDialog({ loading, onCancel, onConfirm, user }) {
  const [reason, setReason] = useState(
    user.activeSessionCount >= 3 || user.activeDeviceCount >= 3
      ? 'Multiple active sessions/devices require admin review'
      : '',
  );
  const trimmedReason = reason.trim();

  return (
    <div className="fixed inset-0 z-[110] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
      <div className="w-full max-w-lg rounded-xl border border-[#E2E8F0] bg-white p-6 shadow-2xl">
        <DialogHeader title={`Ban ${user.displayName}?`} />
        <p className="text-sm font-medium leading-6 text-slate-600">
          Add a clear reason. This reason is saved on the account and helps future admins understand the moderation decision.
        </p>
        <label className="mt-5 block">
          <span className="text-xs font-medium text-[#94A3B8] uppercase tracking-wider">
            Ban reason
          </span>
          <textarea
            className="mt-2 min-h-32 w-full resize-none rounded-lg border border-[#E2E8F0] bg-[#F8FAFC] p-4 text-sm font-medium outline-none focus:ring-2 focus:ring-[#D91F26]/20 focus:border-[#D91F26] transition-all"
            onChange={(event) => setReason(event.target.value)}
            placeholder="Example: Harassment reports, fake profile, spam, or repeated suspicious logins."
            value={reason}
          />
        </label>
        <div className="mt-6 flex justify-end gap-3">
          <button
            className="rounded-lg border border-[#E2E8F0] px-4 py-2.5 text-sm font-semibold text-[#64748B] bg-[#F8FAFC] hover:bg-[#F1F5F9] transition-all"
            disabled={loading}
            onClick={onCancel}
          >
            Cancel
          </button>
          <button
            className="rounded-lg bg-[#D91F26] px-4 py-2.5 text-sm font-semibold text-white hover:bg-red-700 transition-all active:scale-[0.98] disabled:cursor-not-allowed disabled:opacity-50"
            disabled={loading || trimmedReason.length < 3}
            onClick={() => onConfirm(trimmedReason)}
          >
            {loading ? 'Banning...' : 'Ban user'}
          </button>
        </div>
      </div>
    </div>
  );
}

function ConfirmUserDialog({
  confirmLabel,
  loading,
  message,
  onCancel,
  onConfirm,
  title,
}) {
  return (
    <div className="fixed inset-0 z-[110] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
      <div className="w-full max-w-md rounded-xl border border-[#E2E8F0] bg-white p-6 shadow-2xl">
        <DialogHeader title={title} />
        <p className="text-sm font-medium leading-6 text-slate-600">{message}</p>
        <div className="mt-6 flex justify-end gap-3">
          <button
            className="rounded-lg border border-[#E2E8F0] px-4 py-2.5 text-sm font-semibold text-[#64748B] bg-[#F8FAFC] hover:bg-[#F1F5F9] transition-all"
            disabled={loading}
            onClick={onCancel}
          >
            Cancel
          </button>
          <button
            className="rounded-lg bg-emerald-600 px-4 py-2.5 text-sm font-semibold text-white hover:bg-emerald-700 transition-all active:scale-[0.98] disabled:cursor-not-allowed disabled:opacity-50"
            disabled={loading}
            onClick={onConfirm}
          >
            {loading ? 'Saving...' : confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}

function DialogHeader({ title }) {
  return (
    <div className="mb-5 flex items-center gap-3">
      <BrandLogo className="h-10 w-10" />
      <div>
        <p className="text-xs font-medium text-[#94A3B8] uppercase tracking-wider">
          LoversConnect Admin
        </p>
        <h2 className="text-base font-bold text-[#0F172A]">{title}</h2>
      </div>
    </div>
  );
}

function UserDetailsDrawer({ loading, onClose, onToggleBan, user }) {
  const banned = user?.isBanned || user?.status === 'BLOCKED';
  return (
    <div className="fixed inset-0 z-50 flex justify-end bg-black/50">
      <div className="h-full w-full max-w-3xl overflow-y-auto bg-white shadow-2xl">
        <div className="sticky top-0 z-10 flex items-center justify-between border-b border-[#E2E8F0] bg-white/95 p-6 backdrop-blur">
          <div>
            <p className="text-xs font-medium text-[#94A3B8] uppercase tracking-wider">
              User Profile
            </p>
            <h2 className="text-lg font-bold text-[#0F172A]">
              {user?.displayName || 'Loading...'}
            </h2>
          </div>
          <button className="rounded-lg p-2 text-[#94A3B8] hover:bg-[#F1F5F9] transition-colors" onClick={onClose}>
            <X size={20} />
          </button>
        </div>

        {loading || !user ? (
          <div className="m-6 rounded-lg border border-[#E2E8F0] p-10 text-center">
            <Loader2 className="mx-auto animate-spin text-[#94A3B8]" size={24} />
          </div>
        ) : (
          <div className="space-y-6 p-6">
            <div className="grid gap-5 lg:grid-cols-[220px_1fr]">
              <ProfilePhotoStack user={user} />
              <div className="space-y-4">
                <div>
                  <div className="flex flex-wrap items-center gap-2">
                    <p className="text-xl font-bold text-[#0F172A]">{user.displayName}</p>
                    <StatusPill banned={banned} isAdmin={user.isAdmin} status={user.status} />
                  </div>
                  <p className="mt-1 text-sm font-medium text-[#64748B]">{user.email || user.phone}</p>
                </div>
                <p className="rounded-lg bg-[#F8FAFC] border border-[#E2E8F0] p-4 text-sm font-medium leading-6 text-slate-600">
                  {user.bio || 'No bio has been added.'}
                </p>
                <div className="grid grid-cols-3 gap-3">
                  <SignalCard label="Sessions" value={user.activeSessionCount} />
                  <SignalCard label="Devices" value={user.activeDeviceCount} />
                  <SignalCard label="Reports" value={user.reportsAgainstCount} />
                </div>
              </div>
            </div>

            <DetailGrid user={user} />

            <Section title="Profile Photos" icon={<Images size={18} />}>
              {user.photos?.length ? (
                <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
                  {user.photos.map((photo) => (
                    <img
                      alt=""
                      className="aspect-square rounded-lg border border-[#E2E8F0] object-cover"
                      key={photo}
                      src={photo}
                    />
                  ))}
                </div>
              ) : (
                <EmptyText>No profile photos.</EmptyText>
              )}
            </Section>

            <Section title="Login and Device Activity" icon={<Smartphone size={18} />}>
              <div className="grid gap-3 lg:grid-cols-2">
                <ActivityList
                  empty="No refresh sessions."
                  items={(user.refreshTokens || []).map((token) => ({
                    title: token.revokedAt ? 'Revoked session' : 'Active session',
                    meta: `Created ${formatDateTime(token.createdAt)} · Expires ${formatDateTime(token.expiresAt)}`,
                    danger: !token.revokedAt && new Date(token.expiresAt) > new Date(),
                  }))}
                />
                <ActivityList
                  empty="No devices."
                  items={(user.deviceTokens || []).map((device) => ({
                    title: `${device.platform || 'Unknown'} device`,
                    meta: `Last seen ${formatDateTime(device.lastSeenAt)}`,
                    danger: device.isActive,
                  }))}
                />
              </div>
            </Section>

            <Section title="Reports and Moderation" icon={<ShieldAlert size={18} />}>
              <div className="grid gap-3 lg:grid-cols-2">
                <ReportList title="Reports Against" reports={user.reportsAgainst || []} mode="against" />
                <ReportList title="Reports Filed" reports={user.reportsFiled || []} mode="filed" />
              </div>
              {user.banReason && (
                <div className="mt-3 rounded-lg border border-red-200 bg-red-50 p-4 text-sm font-medium text-[#D91F26]">
                  Ban reason: {user.banReason}
                </div>
              )}
            </Section>

            <button
              className={`w-full rounded-lg py-3 text-sm font-semibold text-white transition-all active:scale-[0.98] ${
                banned ? 'bg-emerald-600 hover:bg-emerald-700' : 'bg-[#D91F26] hover:bg-red-700'
              } disabled:cursor-not-allowed disabled:opacity-40`}
              disabled={user.isAdmin}
              onClick={() => onToggleBan(user)}
            >
              {banned ? 'Unban User' : 'Ban User'}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

function ProfilePhotoStack({ user }) {
  return (
    <div className="rounded-lg bg-[#F8FAFC] border border-[#E2E8F0] p-3">
      {user.photo ? (
        <img alt="" className="aspect-[4/5] w-full rounded-lg object-cover" src={user.photo} />
      ) : (
        <div className="flex aspect-[4/5] w-full items-center justify-center rounded-lg bg-[#F1F5F9] text-[#94A3B8]">
          <UserCircle size={56} />
        </div>
      )}
    </div>
  );
}

function DetailGrid({ user }) {
  const items = [
    ['Gender', user.gender || 'Not set'],
    ['Country', user.countryOfResidence || 'Not set'],
    ['Birth Date', formatDate(user.birthDate)],
    ['Created', formatDate(user.createdAt)],
    ['Phone', user.phone || 'Not set'],
    ['Subscription', `${user.subscription?.status || 'NONE'} ${user.subscription?.plan || ''}`],
    ['Subscription Ends', formatDate(user.subscription?.endDate)],
    ['Swipes Sent', user.swipesSentCount],
    ['Swipes Received', user.swipesReceivedCount],
    ['Blocks Received', user.blocksReceivedCount],
    ['Discoverable', user.discoverable === false ? 'No' : 'Yes'],
  ];

  return (
    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-3">
      {items.map(([label, value]) => (
        <div key={label} className="rounded-lg border border-[#E2E8F0] p-4">
          <p className="text-xs font-medium text-[#94A3B8] uppercase tracking-wider">
            {label}
          </p>
          <p className="mt-1 text-sm font-semibold text-[#0F172A]">{value || 'N/A'}</p>
        </div>
      ))}
    </div>
  );
}

function ActivityList({ empty, items }) {
  if (!items.length) return <EmptyText>{empty}</EmptyText>;
  return (
    <div className="space-y-2">
      {items.map((item, index) => (
        <div key={`${item.title}-${index}`} className="rounded-lg border border-[#E2E8F0] p-3">
          <div className="flex items-center justify-between gap-3">
            <p className="text-sm font-semibold text-[#0F172A]">{item.title}</p>
            <span className={`h-2 w-2 rounded-full ${item.danger ? 'bg-emerald-500' : 'bg-[#CBD5E1]'}`} />
          </div>
          <p className="mt-1 text-xs font-medium text-[#94A3B8]">{item.meta}</p>
        </div>
      ))}
    </div>
  );
}

function ReportList({ mode, reports, title }) {
  if (!reports.length) return <EmptyText>{title}: none.</EmptyText>;
  return (
    <div className="space-y-2">
      <p className="text-xs font-medium text-[#94A3B8] uppercase tracking-wider">{title}</p>
      {reports.map((report) => {
        const peer = mode === 'against' ? report.reporter : report.reported;
        return (
          <div key={report.id} className="rounded-lg border border-[#E2E8F0] p-3">
            <p className="text-sm font-semibold text-[#0F172A]">{report.reason}</p>
            <p className="mt-1 text-xs font-medium text-[#94A3B8]">
              {peer?.name || peer?.email || 'Unknown'} · {formatDate(report.createdAt)}
            </p>
            {report.details && <p className="mt-2 text-xs text-[#64748B]">{report.details}</p>}
          </div>
        );
      })}
    </div>
  );
}

function Section({ children, icon, title }) {
  return (
    <section className="rounded-lg border border-[#E2E8F0] p-5">
      <div className="mb-4 flex items-center gap-2 text-[#0F172A]">
        <span className="text-[#94A3B8]">{icon}</span>
        <h3 className="font-semibold">{title}</h3>
      </div>
      {children}
    </section>
  );
}

function SignalCard({ label, value }) {
  const danger = (label === 'Reports' && value > 0) || (label !== 'Reports' && value >= 3);
  return (
    <div className={`rounded-lg p-4 ${danger ? 'bg-red-50 text-[#D91F26]' : 'bg-[#F8FAFC] border border-[#E2E8F0] text-[#0F172A]'}`}>
      <p className="text-xl font-bold">{value || 0}</p>
      <p className="text-[10px] font-medium uppercase tracking-wider opacity-70">{label}</p>
    </div>
  );
}

function AuthPill({ method }) {
  const isOtp = method === 'OTP';
  return (
    <span
      className={`inline-flex rounded-full px-2.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider ${
        isOtp ? 'bg-sky-50 text-sky-700 border border-sky-100' : 'bg-violet-50 text-violet-700 border border-violet-100'
      }`}
    >
      {isOtp ? 'OTP' : 'Email'}
    </span>
  );
}

function RiskSummary({ risky, user }) {
  const value =
    user.reportsAgainstCount > 0
      ? `${user.reportsAgainstCount} reports`
      : `${user.activeSessionCount || 0}/${user.activeDeviceCount || 0}`;
  return (
    <span
      className={`inline-flex rounded-full px-2.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider ${
        risky ? 'bg-red-50 text-[#D91F26] border border-red-100' : 'bg-[#F1F5F9] text-[#64748B] border border-[#E2E8F0]'
      }`}
      title="Sessions/devices or reports"
    >
      {risky ? 'Review' : value}
    </span>
  );
}

function Avatar({ large = false, user }) {
  const size = large ? 'h-16 w-16 rounded-lg' : 'h-10 w-10 rounded-full';
  if (user.photo) {
    return (
      <img
        alt=""
        className={`${size} border border-[#E2E8F0] object-cover`}
        src={user.photo}
      />
    );
  }
  return (
    <div className={`${size} flex items-center justify-center bg-[#F1F5F9] text-[#94A3B8]`}>
      <UserCircle size={large ? 34 : 22} />
    </div>
  );
}

function StatusPill({ banned, isAdmin, status }) {
  if (isAdmin) {
    return (
      <span className="inline-flex items-center gap-1 rounded-full bg-indigo-50 px-2.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-indigo-600 border border-indigo-100">
        <ShieldCheck size={12} />
        Admin
      </span>
    );
  }

  return (
    <span
      className={`inline-flex rounded-full px-2.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider ${
        banned ? 'bg-red-50 text-[#D91F26] border border-red-100' : 'bg-emerald-50 text-emerald-600 border border-emerald-100'
      }`}
    >
      {banned ? 'Banned' : status || 'Active'}
    </span>
  );
}

function EmptyText({ children }) {
  return (
    <p className="rounded-lg bg-[#F8FAFC] border border-[#E2E8F0] p-4 text-sm font-medium text-[#94A3B8]">
      {children}
    </p>
  );
}

function formatDate(value) {
  if (!value) return 'N/A';
  return new Intl.DateTimeFormat('en-MW', {
    year: 'numeric',
    month: 'short',
    day: '2-digit',
  }).format(new Date(value));
}

function formatDateTime(value) {
  if (!value) return 'N/A';
  return new Intl.DateTimeFormat('en-MW', {
    year: 'numeric',
    month: 'short',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  }).format(new Date(value));
}
